<template>
  <v-modal id="providers-summary-modal" class-name="modal-fullscreen-sm-down" size="xl" @closed="$emit('closed')">
    <!-- Header with Switcher -->
    <modal-header class="border-bottom py-12 bg-white sticky-top">
      <div class="container-fluid d-flex align-items-center justify-content-between px-24">
        <div class="d-flex align-items-center gap-12">
          <v-icon icon="info" size="sm" class="text-black" />
          <modal-title class="h6 fw-bold text-uppercase letter-spacing-1 mb-0">
            {{ $t("tourPricing.invoiceDetail") }}
          </modal-title>
        </div>
        
        <div class="d-flex align-items-center gap-16">
          <ul class="nav nav-pills switcher-pills-compact" id="invoiceEmailTab" role="tablist">
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'invoice' }"
                type="button"
                @click="activeTab = 'invoice'"
              >
                {{ $t("general.shared.invoice") }}
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'email' }"
                type="button"
                @click="activeTab = 'email'"
              >
                {{ $t("general.shared.email") }}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </modal-header>

    <modal-body class="p-0 bg-white">
      <!-- Master Config Bar -->
      <div class="master-config-bar-ultra px-24 py-8 border-bottom d-flex align-items-center justify-content-between gap-16 bg-light-soft">
        <div class="d-flex align-items-center gap-8">
          <v-checkbox
            id="invoice-summary-select-all"
            v-model="selectAll"
            label=" "
            class="m-0"
            @change="toggleAllSuppliers"
          />
          <span class="x-small fw-bold text-uppercase text-black">Select All</span>
        </div>
        
        <template v-if="activeTab === 'email'">
          <div class="d-flex gap-8">
            <div style="width: 180px;">
              <v-select
                v-model="masterNotification"
                :clearable="true"
                :options="filteredOptions"
                :placeholder="$t('tourBooking.masterNotification')"
                :reduce="(returnValue: any) => returnValue.key"
                class="v-select-ultra-compact"
              >
                <template v-slot:no-options>
                  <div class="my-4 small">{{ $t("general.vueSelect.noOptions") }}</div>
                </template>
              </v-select>
            </div>

            <div style="width: 180px;">
              <v-select
                v-model="supplierEmail"
                :clearable="false"
                :options="supplierEmailOptions"
                :placeholder="$t('tourBooking.notificationEmail')"
                :reduce="(returnValue: any) => returnValue.key"
                :selectable="isOptionEnabled"
                class="v-select-ultra-compact"
              />
            </div>
          </div>
        </template>
      </div>

      <!-- Main Providers Table -->
      <div class="table-responsive">
        <table class="table table-sm table-hover table-providers-high-density align-middle mb-0">
          <thead>
            <tr class="bg-light text-uppercase x-small fw-bold text-muted border-bottom">
              <th width="40" class="text-center px-0"></th>
              <th width="35%" class="ps-16">Provider Name</th>
              <th width="5%" class="text-center">Wise</th>
              <th width="5%" class="text-center">Holded</th>
              <th v-if="activeTab === 'email'" width="30%">Notification</th>
              <th class="text-end px-16">Net Amount</th>
              <th width="40"></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="supplier in flattenedSuppliers" :key="`${supplier.id}_${supplier.caseDayItemId}`">
              <!-- Main Row -->
              <tr 
                class="supplier-row pointer" 
                :class="{ 'is-expanded': isSupplierOpen(supplier.id), 'active-selection': supplierCheckedMap[`${supplier.id}_${supplier.caseDayItemId}`] }"
                @click="toggleSupplier(supplier.id)"
              >
                <td class="text-center px-0" @click.stop>
                  <v-checkbox
                    :id="`inv-sum-${supplier.id}-${supplier.caseDayItemId}`"
                    v-model="supplierCheckedMap[`${supplier.id}_${supplier.caseDayItemId}`]"
                    label=" "
                    :isDisabled="isSupplierDisabled(supplier)"
                    class="m-0"
                    @change="onSupplierToggle(supplier)"
                  />
                </td>
                <td class="ps-16">
                  <span class="text-dark text-truncate d-block" :title="supplier.name">
                    {{ supplier.name }}
                  </span>
                </td>
                <td class="text-center">
                  <v-icon v-if="supplier.invoicePaymentStatus === 'complete'" icon="check" class="text-success" size="xs" />
                </td>
                <td class="text-center">
                  <v-icon v-if="supplier.isConnectedToHolded" icon="check" class="text-success" size="xs" />
                </td>
                <td v-if="activeTab === 'email'" @click.stop>
                  <div class="pe-16">
                    <v-select
                      v-model="notificationMap[supplier.id]"
                      :clearable="true"
                      :options="filteredOptions"
                      :reduce="(returnValue: any) => returnValue.key"
                      @update:modelValue="() => validateSupplier(supplier)"
                      :class="{ 'is-invalid': supplierErrors[supplier.id] }"
                      class="v-select-ultra-compact w-100"
                    />
                  </div>
                </td>
                <td class="text-end fw-bold px-16">
                  {{ formatPrice(supplier.comonNetPrice ?? supplier.totalNetPrice ?? 0) }} €
                </td>
                <td class="text-center px-0">
                  <v-icon 
                    class="chevron-high-density" 
                    :class="{ 'rotated': isSupplierOpen(supplier.id) }"
                    icon="chevron-down" 
                    size="xs" 
                  />
                </td>
              </tr>

              <!-- Details Row -->
              <tr v-if="isSupplierOpen(supplier.id)" class="detail-row">
                <td colspan="7" class="p-0 border-0">
                  <div class="expansion-high-density bg-light-soft px-32 py-12 border-bottom">
                    <table class="table table-sm table-borderless inner-table-high-density mb-0">
                      <thead>
                        <tr class="text-uppercase x-small fw-bold text-muted border-bottom">
                          <th width="40" class="py-4 text-center ps-16"></th>
                          <th class="py-4 text-start">Service Item</th>
                          <th class="text-center py-4">Qty</th>
                          <th class="text-center py-4">Dates</th>
                          <th class="py-4 text-end">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, i) in supplier.itemResourcesGrouped" :key="i">
                          <td class="py-4 text-center ps-16" @click.stop>
                            <v-checkbox
                              :id="`item-${supplier.id}-${supplier.caseDayItemId}-${i}`"
                              v-model="itemCheckedMap[itemKey(supplier, i)]"
                              label=" "
                              :isDisabled="isItemDisabled(supplier, item)"
                              class="m-0"
                              @change="onItemToggle(supplier, i)"
                            />
                          </td>
                          <td class="py-8 px-8 text-start small fw-bold">
                            {{ item.name }}
                            <div v-if="item.type !== 'hotel' && (item.originName || item.destinationName)" class="x-small text-muted fw-normal mt-1">
                              <template v-if="item.originName">{{ item.originName }}</template>
                              <template v-if="item.originName && item.destinationName"> → </template>
                              <template v-if="item.destinationName">{{ item.destinationName }}</template>
                            </div>
                            <div v-if="item.type !== 'hotel' && (item.initHour || item.endHour)" class="x-small text-muted fw-normal">
                              <template v-if="item.initHour">{{ item.initHour }}</template>
                              <template v-if="item.initHour && item.endHour"> - </template>
                              <template v-if="item.endHour">{{ item.endHour }}</template>
                            </div>
                          </td>
                          <td class="py-8 px-8 text-center small">{{ item.quantity }}</td>
                          <td class="py-8 px-8 text-center x-small text-muted">
                            {{ $str.formatDateTime(item.date, settings.formatDate) }}
                            <template v-if="item.type === 'hotel' && !!item.endDate">
                              — {{ $str.formatDateTime(item.endDate, settings.formatDate) }}
                            </template>
                          </td>
                          <td class="py-8 px-8 text-end small fw-bold">
                            <div class="d-flex align-items-center justify-content-end gap-4">
                              <v-icon
                                v-if="(item as any).paymentStatus === 'complete'"
                                icon="check"
                                size="xs"
                                class="text-success"
                                title="Wise: complete"
                              />
                              <v-icon
                                v-if="(item as any).isConnectedToHolded"
                                icon="check"
                                size="xs"
                                class="text-success"
                                title="Holded: connected"
                              />
                              <span>{{ formatPrice(item.amountPending || item.netPrice) }} €</span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <div class="mt-12 pt-8 border-top d-flex align-items-center gap-12" @click.stop>
                      <span class="x-small fw-bold text-muted text-uppercase">Reference Date:</span>
                      <div class="position-relative">
                        <Datepicker
                          v-model="accountDateMap[supplier.id]"
                          :enable-time-picker="false"
                          auto-apply
                        >
                          <template #trigger>
                            <div class="compact-date-trigger border bg-white px-12 py-4 d-flex align-items-center gap-12 pointer hover-border-dark shadow-xs rounded-sm ms-4">
                              <span class="x-small fw-bold" :class="accountDateMap[supplier.id] ? 'text-black' : 'text-muted opacity-60'">
                                {{ accountDateMap[supplier.id] ? formatDate(accountDateMap[supplier.id]) : 'DD/MM/YYYY' }}
                              </span>
                              <v-icon icon="calendar" size="xs" class="text-muted opacity-60" />
                            </div>
                          </template>
                        </Datepicker>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </modal-body>

    <!-- Modal Footer -->
    <modal-footer class="bg-white border-top py-12 px-0 shadow-sm-top">
      <div class="container-fluid d-flex align-items-center justify-content-between">
        <div class="d-flex gap-24">
          <div class="text-start">
            <div class="x-small fw-bold text-muted text-uppercase mb-1">Net Total</div>
            <div class="fw-bold h6 mb-0 text-black">{{ formatPrice(checkedSupplierTotal) }} €</div>
          </div>
          <div class="text-start">
            <div class="x-small fw-bold text-muted text-uppercase mb-1">Selected</div>
            <div class="h6 fw-bold mb-0 text-black">{{ checkedItemsCount }}</div>
          </div>
        </div>
        
        <div class="d-flex gap-4">
          <v-button variant="outline-dark" size="sm" class="rounded-sm text-uppercase x-small fw-bold px-16 py-4 mx-4" @click="$emit('closed')">
            {{ $t("general.button.close") }}
          </v-button>
          <v-button
            variant="dark"
            size="sm"
            class="rounded-sm text-uppercase x-small fw-bold px-16 py-4 shadow-xs mx-4"
            :disabled="generatePayload.length === 0"
            @click="genrateInvoice"
          >
            {{ activeTab === 'invoice' ? $t("general.button.confirm") : 'Send Notifications' }}
          </v-button>
        </div>
      </div>
    </modal-footer>
  </v-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import vSelect from "vue-select";
import { DateTime } from "luxon";
import { mapState } from "pinia";
import { useAppStore } from "@/stores/app";
import { mapConcepts } from "@/helpers/ConceptHelper";
import { VCheckbox } from "@uniqoders/form";
import useConcepts from "@/helpers/Concepts";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VModal from "@/components/vendor/basic/modal/VModal.vue";
import ModalHeader from "@/components/vendor/basic/modal/ModalHeader.vue";
import ModalTitle from "@/components/vendor/basic/modal/ModalTitle.vue";
import ModalBody from "@/components/vendor/basic/modal/ModalBody.vue";
import ModalFooter from "@/components/vendor/basic/modal/ModalFooter.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import Datepicker from "@vuepic/vue-datepicker";
import api from "@/api";
import { useAuthStore } from "@/stores/auth";
import { formatPrice } from "@/composables/pricing";

type Product = {
  name: string;
  quantity: number;
  date: string;
  endDate: string;
  amountPending: number;
  netPrice: number;
  tourDayItemResourceIds: number[];
  type: string;
  initHour?: string | null;
  endHour?: string | null;
  originName?: string | null;
  originUrl?: string | null;
  destinationName?: string | null;
  destinationUrl?: string | null;
};

type Supplier = {
  invoicePaymentStatus: string;
  paymentStatus: string;
  isConnectedToHolded: boolean;
  tourDayItemIds: any;
  caseDayItemId: any;
  id: string | number;
  name: string;
  comonNetPrice: number;
  totalNetPrice: number;
  netPrice: number;
  itemResourcesGrouped: Product[];
  notification?: string;
  paymentMethod: string;
};

type PricingItem = {
  name: string;
  suppliers: Supplier[];
};

type PayloadProduct = {
  name: string;
  qty: string;
  date: string;
  endDate: string;
  price: number;
  resourceId: number[];
  accountDate?: string;
};

type SupplierPayload = {
  caseDayItemId: string;
  supplierId: string;
  tourDayItemId: string;
  name: string;
  total: string;
  products: PayloadProduct[];
  notification_type?: string | null;
  notification_email?: string | null;
};

export default defineComponent({
  name: "TheProvidersModal",
  components: {
    VModal,
    VButton,
    ModalHeader,
    ModalTitle,
    ModalBody,
    ModalFooter,
    VIcon,
    VCheckbox,
    vSelect,
    Datepicker,
  },
  emits: ["closed", "reloadItems"],
  props: {
    agency: { type: String, required: true },
    caseId: { type: Number, required: true },
    tourId: { type: Number, required: true },
    pricing: { type: Object, required: true },
    paymentMethod: { type: Object, required: false },
  },
  setup() {
    return { ...useConcepts(), formatPrice };
  },
  data() {
    return {
      openSupplierIds: [] as number[],
      supplierCheckedMap: {} as Record<string, boolean>,
      itemCheckedMap: {} as Record<string, boolean>,
      selectAll: false,
      activeTab: "invoice",
      supplierEmail: "",
      notificationMap: {} as Record<string, string>,
      supplierErrors: {} as Record<string, any>,
      masterNotification: "",
      accountDateMap: {} as Record<number, string>,
    };
  },
  watch: {
    supplierCheckedMap: {
      handler(newMap) {
        const keys = Object.keys(newMap);
        if (keys.length === 0) return;
        this.selectAll = keys.every((key) => newMap[key]);
      },
      deep: true,
      immediate: true,
    },
    pricing: {
      handler() {
        if (!this.pricing?.items) return;
        this.pricing.items.forEach((item: PricingItem) => {
          item.suppliers.forEach((supplier) => {
            if (this.notificationMap[supplier.id] === undefined) {
              // Default to booking_request so the user does not accidentally send a modification email
              this.notificationMap[supplier.id] = "booking_request";
            }
            // Initialize item checkboxes to match the supplier checkbox state
            const supplierKey = `${supplier.id}_${supplier.caseDayItemId}`;
            const supplierChecked = this.supplierCheckedMap[supplierKey] ?? false;
            supplier.itemResourcesGrouped.forEach((_: any, i: number) => {
              const iKey = `${supplier.id}_${supplier.caseDayItemId}_${i}`;
              if (this.itemCheckedMap[iKey] === undefined) {
                this.itemCheckedMap[iKey] = supplierChecked;
              }
            });
          });
        });
      },
      immediate: true,
    },
    masterNotification(val) {
      if (!val || !this.pricing?.items) return;
      this.pricing.items.forEach((item: PricingItem) => {
        item.suppliers.forEach((supplier) => {
          this.notificationMap[supplier.id] = val;
          this.validateSupplier(supplier);
        });
      });
    },
  },
  computed: {
    ...mapState(useAppStore, ["settings"]),
    ...mapState(useAuthStore, ["user"]),
    flattenedSuppliers(): Supplier[] {
      if (!this.pricing?.items) return [];
      const suppliers: Supplier[] = [];
      this.pricing.items
        .filter((item: PricingItem) => !item.name.startsWith('null') && !item.name.startsWith('unknown'))
        .forEach((item: PricingItem) => {
          item.suppliers.forEach((s: Supplier) => suppliers.push(s));
        });
      return suppliers;
    },
    itemTypes(): any {
      if (!!this.concepts && this.concepts["supplier.type"]) {
        return mapConcepts(this.concepts["supplier.type"]);
      }
      return [];
    },
    supplierEmailNotificationOptions(): any[] {
      if (!!this.concepts && this.concepts["supplier.email_notification_types"]) {
        return mapConcepts(this.concepts["supplier.email_notification_types"], true);
      }
      return [];
    },
    filteredOptions() {
      return this.supplierEmailNotificationOptions.filter((option) => option.key !== "booking_request_modification");
    },
    supplierEmailOptions(): any[] {
      return [
        { key: "booking@waw.travel", label: "booking@waw.travel" },
        { key: this.user?.email || "", label: this.user?.email || "" },
      ];
    },
    generatePayload(): SupplierPayload[] {
      if (!this.pricing?.items) return [];
      const payload: SupplierPayload[] = this.pricing.items
        .filter((item: PricingItem) => !item.name.startsWith("null") && !item.name.startsWith("unknown"))
        .reduce((acc: SupplierPayload[], type: PricingItem) => {
          type.suppliers.forEach((supplier: Supplier) => {
            // Only include items that are individually checked and not disabled
            const checkedItems = supplier.itemResourcesGrouped
              .map((product: Product, i: number) => ({ product, i }))
              .filter(({ product, i }) =>
                this.itemCheckedMap[`${supplier.id}_${supplier.caseDayItemId}_${i}`]
                && !this.isItemDisabled(supplier, product)
              )
              .map(({ product }) => product);

            if (checkedItems.length === 0) return;

            const supplierTotal = checkedItems.reduce(
              (sum: number, p: Product) => sum + (p.amountPending || p.netPrice),
              0
            );

            const supplierPayload: SupplierPayload = {
              supplierId: String(supplier.id),
              name: supplier.name,
              total: String(Math.round(supplierTotal * 100) / 100),
              tourDayItemId: String(supplier.tourDayItemIds),
              caseDayItemId: String(supplier.caseDayItemId),
              ...(this.activeTab === "email"
                ? {
                    notification_type: this.notificationMap?.[supplier.id] ?? null,
                    notification_email: this.supplierEmail ?? null,
                  }
                : {}),
              products: checkedItems.map((product: Product): PayloadProduct => {
                return {
                  name: product.name,
                  qty: String(product.quantity),
                  date: product.date,
                  endDate: product.endDate,
                  accountDate: this.accountDateMap[supplier.id] ? this.formatDate(this.accountDateMap[supplier.id]) : undefined,
                  price: product.amountPending || product.netPrice,
                  resourceId: product.tourDayItemResourceIds,
                };
              }),
            };
            acc.push(supplierPayload);
          });
          return acc;
        }, []);
      return payload;
    },
    checkedSupplierTotal() {
      let total = 0;
      if (!this.pricing?.items) return "0.00";
      this.pricing.items.forEach((type: { suppliers: any[] }) => {
        type.suppliers.forEach((supplier: Supplier) => {
          supplier.itemResourcesGrouped.forEach((item: Product, i: number) => {
            if (!this.itemCheckedMap[`${supplier.id}_${supplier.caseDayItemId}_${i}`]) return;
            if (this.isItemDisabled(supplier, item)) return;
            total += Number(item.amountPending || item.netPrice) || 0;
          });
        });
      });
      return Math.round(total * 100) / 100;
    },
    checkedItemsCount() {
      if (!this.pricing?.items) return 0;
      let count = 0;
      this.pricing.items.forEach((type: { suppliers: any[] }) => {
        type.suppliers.forEach((supplier: Supplier) => {
          supplier.itemResourcesGrouped.forEach((item: any, i: number) => {
            if (this.itemCheckedMap[`${supplier.id}_${supplier.caseDayItemId}_${i}`] && !this.isItemDisabled(supplier, item)) {
              count++;
            }
          });
        });
      });
      return count;
    },
  },
  methods: {
    formatDate(date: Date | string | null) {
      if (!date) return "";
      return DateTime.fromISO(new Date(date).toISOString()).toFormat("dd/MM/yyyy");
    },
    isSupplierDisabled(supplier: Supplier) {
      // On the email tab, suppliers are always selectable so notifications can be re-sent.
      if (this.activeTab === "email") return false;
      // On the invoice tab, disable the supplier only if ALL items are complete+Holded.
      // If at least one item is still payable, the supplier remains selectable for partial invoice.
      if (!supplier.itemResourcesGrouped || supplier.itemResourcesGrouped.length === 0) {
        return supplier.isConnectedToHolded && supplier.invoicePaymentStatus === "complete";
      }
      return supplier.itemResourcesGrouped.every(
        (item: any) => item.paymentStatus === "complete" && item.isConnectedToHolded
      );
    },
    isItemDisabled(supplier: Supplier, item: any) {
      // On the email tab, items are always selectable.
      if (this.activeTab === "email") return false;
      // On the invoice tab, disable only items that are individually complete+Holded.
      return item.paymentStatus === "complete" && item.isConnectedToHolded;
    },
    itemKey(supplier: Supplier, index: number) {
      return `${supplier.id}_${supplier.caseDayItemId}_${index}`;
    },
    onSupplierToggle(supplier: Supplier) {
      const supplierKey = `${supplier.id}_${supplier.caseDayItemId}`;
      const checked = this.supplierCheckedMap[supplierKey];
      // Sync all non-disabled item checkboxes to match the supplier checkbox
      supplier.itemResourcesGrouped.forEach((item: any, i: number) => {
        if (!this.isItemDisabled(supplier, item)) {
          this.itemCheckedMap[`${supplier.id}_${supplier.caseDayItemId}_${i}`] = checked;
        }
      });
      this.validateSupplier(supplier);
    },
    onItemToggle(supplier: Supplier, itemIndex: number) {
      // Re-evaluate the supplier checkbox based on non-disabled item states
      const allChecked = supplier.itemResourcesGrouped.every((item: any, i: number) => {
        if (this.isItemDisabled(supplier, item)) return true; // disabled items don't affect the state
        return this.itemCheckedMap[`${supplier.id}_${supplier.caseDayItemId}_${i}`];
      });
      const supplierKey = `${supplier.id}_${supplier.caseDayItemId}`;
      this.supplierCheckedMap[supplierKey] = allChecked;
      this.validateSupplier(supplier);
    },
    validateSupplier(supplier: any) {
      const isChecked = this.supplierCheckedMap[`${supplier.id}_${supplier.caseDayItemId}`];
      const selectedNotification = this.notificationMap[supplier.id];
      if (isChecked && !selectedNotification && this.activeTab === 'email') {
        this.supplierErrors[supplier.id] = "Required";
      } else {
        this.supplierErrors[supplier.id] = null;
      }
    },
    isOptionEnabled(option: { label: string }) { return /@waw\.travel$/.test(option.label); },
    getDayName(date: string) { return DateTime.fromISO(date).toFormat("cccc"); },
    toggleSupplier(id: any) {
      const index = this.openSupplierIds.indexOf(id);
      if (index > -1) this.openSupplierIds.splice(index, 1);
      else this.openSupplierIds.push(id);
    },
    isSupplierOpen(id: any) { return this.openSupplierIds.includes(id); },
    async genrateInvoice() {
      const payload = this.generatePayload;
      let confirmationText = this.$t("tourPricing.confirmInvoiceMessage");
      const zeroPriceSuppliers = payload.filter((supplier: any) => Number(supplier.total) === 0).length;
      if (zeroPriceSuppliers > 0) {
        confirmationText = this.$t("tourPricing.confirmInvoiceMessageWithZeroSuppliers", {
          zero: zeroPriceSuppliers,
          total: payload.length,
          message: `${this.$t("tourPricing.confirmInvoiceMessage")}. ${this.$t("tourPricing.confirmMessage")}`,
        });
      }
      await this.$modal.confirm({
        title: this.$t("tourPricing.invoiceDetail"),
        text: confirmationText,
        leftButtonClasses: "btn-tertiary",
        confirmButtonText: this.$t("general.button.confirm"),
        confirmButtonCallback: () => this.confirmInvoice(),
      });
    },
    async confirmInvoice() {
      try {
        if (this.activeTab === "email") await api.case.caseSendEmail(this.agency, this.caseId, this.generatePayload);
        else await api.case.caseInvoice(this.agency, this.caseId, this.generatePayload);
        this.$toast.success(this.$t("general.shared.savedChanges"));
        this.$emit("reloadItems");
        this.$emit("closed");
      } catch (error: any) {
        this.$toast.error(error.response?.data?.message || this.$t("general.shared.somethingWentWrong"));
      }
    },
    toggleAllSuppliers() {
      if (!this.pricing?.items) return;
      this.pricing.items.forEach((type: PricingItem) => {
        type.suppliers.forEach((supplier: Supplier) => {
          if (this.isSupplierDisabled(supplier)) return;
          const supplierKey = `${supplier.id}_${supplier.caseDayItemId}`;
          this.supplierCheckedMap[supplierKey] = this.selectAll;
          supplier.itemResourcesGrouped.forEach((item: any, i: number) => {
            if (!this.isItemDisabled(supplier, item)) {
              this.itemCheckedMap[`${supplier.id}_${supplier.caseDayItemId}_${i}`] = this.selectAll;
            }
          });
          this.validateSupplier(supplier);
        });
      });
    },
  },
  mounted() {
    // Default to the shared booking email, not the logged-in user's email
    this.supplierEmail = this.supplierEmailOptions[0]?.key ?? "";
    // Default the master notification to booking_request
    this.masterNotification = "booking_request";
  },
});
</script>

<style scoped lang="scss">
.x-small { font-size: 0.75rem; }
.bg-light-soft { background-color: #f8fafc; }
.shadow-sm-top { box-shadow: 0 -4px 12px rgba(0,0,0,0.05); }

/* Switcher Pills V2 */
.switcher-pills-compact {
  background-color: #f1f5f9;
  padding: 2px;
  border-radius: 6px;
  .nav-link {
    border-radius: 4px;
    padding: 4px 16px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #64748b;
    border: none;
    &.active {
      background-color: #fff;
      color: #000;
      box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    }
  }
}

/* Main Table Styling - High Density */
.table-providers-high-density {
  thead th {
    background-color: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    padding: 6px 12px;
    color: #94a3b8;
    font-size: 0.75rem;
  }
  tbody tr.supplier-row {
    td { padding: 4px 0; border-bottom: 1px solid #f1f5f9; font-size: 0.8rem; }
    &:hover { background-color: #f8fafc; }
    &.active-selection { background-color: #fcfcfd; }
  }
}

/* Date Trigger Styling */
.compact-date-trigger {
  min-height: 28px;
  border-radius: 4px;
  transition: all 0.2s ease;
  &:hover { border-color: #000 !important; }
}

.hover-border-dark:hover { border-color: #000 !important; }

/* Accordion Animation */
.chevron-high-density { transition: transform 0.2s; &.rotated { transform: rotate(180deg); } }

/* Inner Expansion Table */
.inner-table-high-density {
  thead th { font-size: 0.75rem; color: #94a3b8; border-bottom: 1px dashed #e2e8f0; }
  tbody td { padding: 4px 0; border-bottom: 1px dashed #f1f5f9; font-size: 0.8rem; }
}

.border-bottom-dashed { border-bottom: 1px dashed #f1f5f9; }

/* v-select ultra compact */
.v-select-ultra-compact :deep(.vs__dropdown-toggle) {
  min-height: 26px;
  border-radius: 2px;
  padding: 0;
  font-size: 0.8rem;
  border: 1px solid #e2e8f0;
}
.v-select-ultra-compact :deep(.vs__selected-options) { padding: 0 4px; }
.v-select-ultra-compact :deep(.vs__actions) { padding: 0 2px; }

.v-select-ultra-compact :deep(.vs__dropdown-menu) {
  font-size: 0.8rem;
  border-radius: 2px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.v-select-ultra-compact :deep(.vs__dropdown-option) {
  padding: 4px 8px;
}

.v-select-ultra-compact :deep(.vs__dropdown-option--highlight) {
  background: #000;
  color: #fff;
}

.animate-fade-in { animation: fadeIn 0.2s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
</style>
